import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { doc, getDoc, collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { supabase } from "../../supabaseClient";
import styles from "./CareerOverview.module.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// --- CONFIGURATION ---
const WEB3FORMS_ACCESS_KEY = "d1409213-917e-42aa-b0cc-c16dfa8e5a56"; 
const OWNER_EMAIL = "info@dimorapune.com"; 

const CareerOverview = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [job, setJob] = useState(null);
    const [relatedJobs, setRelatedJobs] = useState([]);
    const [resume, setResume] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchJobData = async () => {
            if (!id) return;
            try {
                setLoading(true);
                const docRef = doc(db, "jobs", id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const jobData = { id: docSnap.id, ...docSnap.data() };
                    setJob(jobData);

                    const snap = await getDocs(collection(db, "jobs"));
                    const list = snap.docs
                        .map(d => ({ id: d.id, ...d.data() }))
                        .filter(item => item.id !== id && item.status === "open")
                        .slice(0, 2);
                    setRelatedJobs(list);
                } else {
                    navigate("/careers");
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchJobData();
    }, [id, navigate]);

const sendEmailNotification = async (appData, resumeUrl) => {
        const emailData = new FormData();
        
        // Essential Config
        emailData.append("access_key", WEB3FORMS_ACCESS_KEY);
        emailData.append("to_email", OWNER_EMAIL);
        emailData.append("from_name", "Career Portal");
        emailData.append("subject", `New Application: ${job.role} - ${appData.firstName}`);

        // --- CLEAN ONE-LINE FORMAT ---
        // We use the Label itself as the Key. 
        // This removes the blue "1, 2, 3" and puts the text on one line.
        emailData.append("Applied For", job.role);
        emailData.append("Full Name", `${appData.firstName} ${appData.lastName}`);
        emailData.append("Email Address", appData.email);
        emailData.append("Phone Number", appData.phone);
        emailData.append("Total Experience", appData.experience);
        emailData.append("Gender", appData.gender);
        emailData.append("Additional Message", appData.message || "None");
        
        // Professional Resume Link
        emailData.append("Resume PDF", `Link: ${resumeUrl}`);

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: emailData
            });
            const result = await response.json();
            if (result.success) {
                console.log("Email sent in clean format.");
            }
        } catch (err) {
            console.error("Email Error:", err);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.type !== "application/pdf") {
            setError("Only PDF files are allowed.");
            setResume(null);
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError("File size must be less than 5MB.");
            setResume(null);
            return;
        }
        setError("");
        setResume(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!resume) {
            setError("Please upload your resume.");
            return;
        }

        setIsSubmitting(true);
        const formElement = e.target;
        const formData = new FormData(formElement);

        try {
            // 1. Upload PDF to Supabase Storage
            const fileExt = resume.name.split('.').pop();
            const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
            const filePath = `resumes/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('career-applications')
                .upload(filePath, resume);

            if (uploadError) throw uploadError;

            // 2. Get Public URL
            const { data: { publicUrl } } = supabase.storage
                .from('career-applications')
                .getPublicUrl(filePath);

            const applicationData = {
                jobId: id,
                appliedFor: job.role,
                firstName: formData.get("firstName").substring(0, 50),
                lastName: formData.get("lastName").substring(0, 50),
                email: formData.get("email").substring(0, 50),
                phone: formData.get("phone").substring(0, 50),
                experience: formData.get("experience").substring(0, 50),
                gender: formData.get("gender"),
                message: formData.get("message").substring(0, 250),
                resumeUrl: publicUrl,
                status: "new",
                createdAt: serverTimestamp(),
            };

            // 3. Save Record to Firestore
            await addDoc(collection(db, "applications"), applicationData);

            // 4. Send Email Notification
            await sendEmailNotification(applicationData, publicUrl);

            alert("Application submitted successfully!");
            formElement.reset();
            setResume(null);
        } catch (err) {
            console.error("Submission error:", err);
            alert("Failed to submit application. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                <h2>Loading Position Details...</h2>
            </div>
        );
    }

    if (!job) return null;

    return (
        <div className={styles.page}>
            <section className={styles.container}>
                <div className={styles.left}>
                    <span className={styles.tag}>OPEN POSITION</span>
                    <h1 className={styles.title}>{job.role?.toUpperCase()}</h1>

                    <div className={styles.meta}>
                        <div><span>LOCATIONS</span><p>{job.location}</p></div>
                        <div><span>JOB TYPE</span><p>{job.type}</p></div>
                        <div><span>YEAR SALARY</span><p>{job.salary}</p></div>
                        <div><span>MIN EXPERIENCE</span><p>{job.experience || "Freshers Welcome"}</p></div>
                        {job.openings > 0 && <div><span>JOB OPENINGS</span><p>{job.openings} Positions</p></div>}
                    </div>

                    <hr className={styles.line} />
                    <h2>WHO WE ARE</h2><p>{job.whoWeAre}</p>
                    <h2>WHAT WE’RE LOOKING FOR</h2><p>{job.lookingFor}</p>
                    {job.description && (
                        <><h2>JOB DESCRIPTION</h2><p>{job.description}</p></>
                    )}
                </div>

                <div className={styles.formCard}>
                    <h3>APPLY FOR THIS POSITION</h3>
                    <form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.row}>
                            <input name="firstName" placeholder="First name" maxLength="50" required />
                            <input name="lastName" placeholder="Last name" maxLength="50" required />
                        </div>
                        <div className={styles.row}>
                            <input name="email" type="email" placeholder="Email address" maxLength="50" required />
                            <input name="phone" type="tel" placeholder="Phone number" maxLength="50" required />
                        </div>
                        <div className={styles.row}>
                            <input name="experience" placeholder="Experience (e.g. 2 Years)" maxLength="50" required />
                            <select name="gender" className={styles.selectInput} required defaultValue="">
                                <option value="" disabled hidden>Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <textarea name="message" placeholder="Additional information (Max 250 chars)" maxLength="250" />

                        <div className={styles.uploadBox}>
                            <label className={styles.uploadLabel}>
                                Upload Resume (PDF only, max 5MB)
                                <input type="file" accept="application/pdf" onChange={handleFileChange} hidden />
                            </label>
                            {resume && <p className={styles.fileName}>Selected: {resume.name}</p>}
                            {error && <p className={styles.error}>{error}</p>}
                        </div>

                        <button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "SUBMITTING..." : "SUBMIT APPLICATION"}
                        </button>
                    </form>
                </div>
            </section>

            {relatedJobs.length > 0 && (
                <section className={styles.section}>
                    <div className={styles.header}>
                        <h2>RELATED JOBS</h2>
                        <button className={styles.primaryBtn} onClick={() => navigate("/careers")}>VIEW OPENING JOBS</button>
                    </div>
                    <div className={styles.cards}>
                        {relatedJobs.map(rJob => (
                            <div key={rJob.id} className={styles.card}>
                                <div>
                                    <h3>{rJob.role?.toUpperCase()}</h3>
                                    <p>{rJob.location?.length > 20 ? `${rJob.location.substring(0, 20)}...` : rJob.location}</p>
                                </div>
                                <button className={styles.outlineBtn} onClick={() => navigate(`/career-overview/${rJob.id}`)}>VIEW DETAILS</button>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default CareerOverview;













// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { db } from "../../firebase";
// import { doc, getDoc, collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
// import { supabase } from "../../supabaseClient";
// import styles from "./CareerOverview.module.css";

// const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// // --- CALLMEBOT CONFIGURATION ---
// const WHATSAPP_CONFIG = {
//   OWNER_PHONE: "91XXXXXXXXXX", // Replace with owner's phone (e.g. 919876543210)
//   API_KEY: "123456",           // Replace with owner's CallMeBot API Key
// };

// const CareerOverview = () => {
//     const { id } = useParams();
//     const navigate = useNavigate();

//     const [job, setJob] = useState(null);
//     const [relatedJobs, setRelatedJobs] = useState([]);
//     const [resume, setResume] = useState(null);
//     const [error, setError] = useState("");
//     const [loading, setLoading] = useState(true);
//     const [isSubmitting, setIsSubmitting] = useState(false);

//     useEffect(() => {
//         const fetchJobData = async () => {
//             if (!id) return;
//             try {
//                 setLoading(true);
//                 const docRef = doc(db, "jobs", id);
//                 const docSnap = await getDoc(docRef);

//                 if (docSnap.exists()) {
//                     const jobData = { id: docSnap.id, ...docSnap.data() };
//                     setJob(jobData);

//                     const snap = await getDocs(collection(db, "jobs"));
//                     const list = snap.docs
//                         .map(d => ({ id: d.id, ...d.data() }))
//                         .filter(item => item.id !== id && item.status === "open")
//                         .slice(0, 2);
//                     setRelatedJobs(list);
//                 } else {
//                     navigate("/careers");
//                 }
//             } catch (err) {
//                 console.error("Fetch Error:", err);
//             } finally {
//                 setLoading(false);
//             }
//         };

//         fetchJobData();
//     }, [id, navigate]);

//     // WhatsApp Notification Helper
//     const sendWhatsAppNotification = async (appData, resumeUrl) => {
//         const text = `*New Job Application!* 💼\n\n` +
//                      `*Position:* ${job.role}\n` +
//                      `*Applicant:* ${appData.firstName} ${appData.lastName}\n` +
//                      `*Experience:* ${appData.experience}\n` +
//                      `*Phone:* ${appData.phone}\n\n` +
//                      `*View Resume:* ${resumeUrl}`;

//         const url = `https://api.callmebot.com/whatsapp.php?phone=${WHATSAPP_CONFIG.OWNER_PHONE}&text=${encodeURIComponent(text)}&apikey=${WHATSAPP_CONFIG.API_KEY}`;

//         try {
//             await fetch(url, { mode: 'no-cors' });
//             console.log("Owner notified of new application.");
//         } catch (err) {
//             console.error("WhatsApp Error:", err);
//         }
//     };

//     const handleFileChange = (e) => {
//         const file = e.target.files[0];
//         if (!file) return;
//         if (file.type !== "application/pdf") {
//             setError("Only PDF files are allowed.");
//             setResume(null);
//             return;
//         }
//         if (file.size > MAX_FILE_SIZE) {
//             setError("File size must be less than 5MB.");
//             setResume(null);
//             return;
//         }
//         setError("");
//         setResume(file);
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         if (!resume) {
//             setError("Please upload your resume.");
//             return;
//         }

//         setIsSubmitting(true);
//         const formElement = e.target;
//         const formData = new FormData(formElement);

//         try {
//             // 1. Upload PDF to Supabase Storage
//             const fileExt = resume.name.split('.').pop();
//             const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
//             const filePath = `resumes/${fileName}`;

//             const { data, error: uploadError } = await supabase.storage
//                 .from('career-applications')
//                 .upload(filePath, resume);

//             if (uploadError) throw uploadError;

//             // 2. Get Public URL
//             const { data: { publicUrl } } = supabase.storage
//                 .from('career-applications')
//                 .getPublicUrl(filePath);

//             const applicationData = {
//                 jobId: id,
//                 appliedFor: job.role,
//                 firstName: formData.get("firstName"),
//                 lastName: formData.get("lastName"),
//                 email: formData.get("email"),
//                 phone: formData.get("phone"),
//                 experience: formData.get("experience"),
//                 gender: formData.get("gender"),
//                 message: formData.get("message"),
//                 resumeUrl: publicUrl,
//                 status: "new",
//                 createdAt: serverTimestamp(),
//             };

//             // 3. Save to Firestore
//             await addDoc(collection(db, "applications"), applicationData);

//             // 4. Send WhatsApp Notification (Including PDF URL)
//             await sendWhatsAppNotification(applicationData, publicUrl);

//             alert("Application submitted successfully!");
//             formElement.reset();
//             setResume(null);
//         } catch (err) {
//             console.error("Submission error:", err);
//             alert("Failed to submit application. Please try again.");
//         } finally {
//             setIsSubmitting(false);
//         }
//     };

//     // ... [Rest of the component's JSX remains the same as your original code] ...
//     if (loading) {
//         return (
//             <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
//                 <h2>Loading Position Details...</h2>
//             </div>
//         );
//     }

//     if (!job) return null;

//     return (
//         <div className={styles.page}>
//             <section className={styles.container}>
//                 <div className={styles.left}>
//                     <span className={styles.tag}>OPEN POSITION</span>
//                     <h1 className={styles.title}>{job.role?.toUpperCase()}</h1>

//                     <div className={styles.meta}>
//                         <div>
//                             <span>LOCATIONS</span>
//                             <p>{job.location}</p>
//                         </div>
//                         <div>
//                             <span>JOB TYPE</span>
//                             <p>{job.type}</p>
//                         </div>
//                         <div>
//                             <span>YEAR SALARY</span>
//                             <p>{job.salary}</p>
//                         </div>
//                         <div>
//                             <span>MIN EXPERIENCE</span>
//                             <p>{job.experience || "Freshers Welcome"}</p>
//                         </div>
//                         {job.openings && job.openings > 0 && (
//                             <div>
//                                 <span>JOB OPENINGS</span>
//                                 <p>{job.openings} Positions</p>
//                             </div>
//                         )}
//                     </div>

//                     <hr className={styles.line} />

//                     <h2>WHO WE ARE</h2>
//                     <p>{job.whoWeAre}</p>

//                     <h2>WHAT WE’RE LOOKING FOR</h2>
//                     <p>{job.lookingFor}</p>

//                     {job.description && (
//                         <>
//                             <h2>JOB DESCRIPTION</h2>
//                             <p>{job.description}</p>
//                         </>
//                     )}
//                 </div>

//                 <div className={styles.formCard}>
//                     <h3>APPLY FOR THIS POSITION</h3>
//                     <form className={styles.form} onSubmit={handleSubmit}>
//                         <div className={styles.row}>
//                             <input name="firstName" placeholder="First name" required />
//                             <input name="lastName" placeholder="Last name" required />
//                         </div>
//                         <div className={styles.row}>
//                             <input name="email" type="email" placeholder="Email address" required />
//                             <input name="phone" type="tel" placeholder="Phone number" required />
//                         </div>

//                         <div className={styles.row}>
//                             <input
//                                 name="experience"
//                                 placeholder="Experience (e.g. 2 Years)"
//                                 required
//                             />
//                             <select
//                                 name="gender"
//                                 className={styles.selectInput}
//                                 required
//                                 defaultValue=""
//                             >
//                                 <option value="" disabled hidden>Select Gender</option>
//                                 <option value="Male">Male</option>
//                                 <option value="Female">Female</option>
//                                 <option value="Other">Other</option>
//                             </select>
//                         </div>
//                         <textarea name="message" placeholder="Additional information" />

//                         <div className={styles.uploadBox}>
//                             <label className={styles.uploadLabel}>
//                                 Upload Resume (PDF only, max 5MB)
//                                 <input type="file" accept="application/pdf" onChange={handleFileChange} hidden />
//                             </label>
//                             {resume && <p className={styles.fileName}>Selected: {resume.name}</p>}
//                             {error && <p className={styles.error}>{error}</p>}
//                         </div>

//                         <button type="submit" disabled={isSubmitting}>
//                             {isSubmitting ? "SUBMITTING..." : "SUBMIT APPLICATION"}
//                         </button>
//                     </form>
//                 </div>
//             </section>

//             {relatedJobs.length > 0 && (
//                 <section className={styles.section}>
//                     <div className={styles.header}>
//                         <h2>RELATED JOBS</h2>
//                         <button className={styles.primaryBtn} onClick={() => navigate("/careers")}>VIEW OPENING JOBS</button>
//                     </div>

//                     <div className={styles.cards}>
//                         {relatedJobs.map(rJob => (
//                             <div key={rJob.id} className={styles.card}>
//                                 <div>
//                                     <h3>{rJob.role?.toUpperCase()}</h3>
//                                     <p title={rJob.location}>
//                                         {rJob.location?.length > 20
//                                             ? `${rJob.location.substring(0, 20)}...`
//                                             : rJob.location}
//                                     </p>
//                                 </div>
//                                 <button className={styles.outlineBtn} onClick={() => navigate(`/career-overview/${rJob.id}`)}>
//                                     VIEW DETAILS
//                                 </button>
//                             </div>
//                         ))}
//                     </div>
//                 </section>
//             )}
//         </div>
//     );
// };

// export default CareerOverview;